import { Injectable, InjectionToken, Injector, Renderer2, inject } from '@angular/core'
import { DynamicElementSpecification } from '../../models/dynamic-element/dynamic-element-specification'
import { DynamicElementConfig, DynamicElementConfigs, DYNAMIC_ELEMENT_CONFIGS } from '../../models/dynamic-element-config'
import { DynamicElementInterceptor, DynamicElementInterceptorOptions } from '../interceptors/dynamic-element-interceptor'

/**
 * Shortcut for a type with a resolved {@link DynamicElementInterceptor }, and any of it's configs
 *
 * @internal
 */
interface InterceptorWithConfig {
  interceptor: DynamicElementInterceptor
  config: unknown
}

/**
 * A configurable service that builds dynamically generated HTML elements.
 *
 * By importing {@link DynamicElementModule.forRoot } with {@link DynamicElementConfigs }, this service is configured
 * to generate HTML elements accordingly.
 *
 * @public
 *
 * @example
 * An example of how to configure the service
 * ```
 * let dynamicElementConfigs = [
 *   {
 *     type: 'PARAGRAPH',
 *     name: 'p'
 *   },
 *   {
 *     type: 'INPUT',
 *     name: 'input',
 *     defaultClasses: [ 'foo', 'bar' ]
 *   },
 *   {
 *     type: 'SELECT',
 *     name: 'select'
 *   }
 * ]
 *
 * @NgModule({
 *   imports: [ DynamicElementModule.forRoot(dynamicElementConfigs) ]
 * })
 * export class AppModule {}
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DynamicElements {

  private readonly _configs: DynamicElementConfigs[] = inject(DYNAMIC_ELEMENT_CONFIGS)

  /**
   * Build an element to the provided specification
   *
   * @param injector  The injector context of where the service is called
   * @param args      The specification of the element we want to dynamically create
   *
   * @returns         The created element
   */
  build(injector: Injector, args: DynamicElementSpecification): HTMLElement {
    // Get the renderer
    let renderer2 = injector.get<Renderer2>(Renderer2)

    // Get the config
    let config = this.getConfig(args)

    // Get the interceptors
    let interceptors = this.getInterceptors(injector, config)

    // Hook to perform additional modifications to the args
    let updatedArgs = this.interceptBeforeBuild(injector, interceptors, args)

    // Create the element
    let name = typeof config.name === 'function' ?
      config.name(updatedArgs) :
      config.name

    let element = renderer2.createElement(name)

    // Add classes and styling
    let defaultClasses = this.getDefaultClasses(config)
    let classes = updatedArgs.classes?.split(/\s/) ?? defaultClasses
    classes
      ?.filter(it => it.trim() !== '')
      ?.forEach(it => renderer2.addClass(element, it))

    // Add attributes
    renderer2.setAttribute(element, 'id', updatedArgs.id)

    // Assign the data to the attributes of the element
    for (let key in updatedArgs.data) {
      // Convert name to attribute safe name
      let name = key.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()

      // If there is a value, assign it as the matching attribute
      if (updatedArgs.data[key] !== null) {
        // Stringify value (Including turning objects to JSON)
        let value = updatedArgs.data[key]
        value = typeof value === 'object' ? JSON.stringify(value) : value

        renderer2.setAttribute(element, name, value as string)
      }

      // Else assign an empty attribute
      else {
        renderer2.setAttribute(element, name, '')
      }
    }

    // Hook to perform more additional modifications to the element
    element = this.interceptAfterBuild(injector, interceptors, updatedArgs, element)

    // Build any child elements
    args?.children?.forEach(it => {
      let childElement = this.build(injector, it)
      renderer2.appendChild(element, childElement)
    })

    // Hook to perform more additional modifications to the element, but now after the children have been built
    element = this.interceptAfterChildrenBuilt(injector, interceptors, updatedArgs, element)

    return element
  }

  /**
   * Intercept the build process of the element before it creates the element, and apply additional modifications
   *
   * @internal
   *
   * @param injector      The injector context of where the service is called
   * @param interceptors  Collection of interceptors that will be performed
   * @param args          The specification of the element we want to dynamically create
   *
   * @returns             The modified specification of the element
   */
  interceptBeforeBuild(injector: Injector, interceptors: InterceptorWithConfig[], args: DynamicElementSpecification): DynamicElementSpecification {
    if (!interceptors) {
      return args
    }

    return interceptors.reduce(
      (nextArgs, it) => it.interceptor?.beforeBuild?.(injector, nextArgs, it.config) ?? nextArgs,
      args
    )
  }

  /**
   * Intercept the build process of the element after it creates the element, and apply additional modifications
   *
   * @internal
   *
   * @param injector      The injector context of where the service is called
   * @param interceptors  Collection of interceptors that will be performed
   * @param args          The specification of the element we want to dynamically create
   * @param element       The pending HTMLElement created for the provided spec
   *
   * @returns             The modified HTMLElement created for the provided spec
   */
  interceptAfterBuild(injector: Injector, interceptors: InterceptorWithConfig[], args: DynamicElementSpecification, element: HTMLElement): HTMLElement {
    if (!interceptors) {
      return element
    }

    return interceptors?.reduce(
      (nextElement, it) => it.interceptor?.afterBuild?.(injector, args, nextElement, it?.config) ?? nextElement,
      element
    )
  }

  /**
   * Intercept the build process of the element after it creates the element, and all of it's possible child elements,
   * then apply additional modifications
   *
   * @internal
   *
   * @param injector      The injector context of where the service is called
   * @param interceptors  Collection of interceptors that will be performed
   * @param args          The specification of the element we want to dynamically create
   * @param element       The pending HTMLElement created for the provided spec
   *
   * @returns             The modified HTMLElement created for the provided spec
   */
  interceptAfterChildrenBuilt(injector: Injector, interceptors: InterceptorWithConfig[], args: DynamicElementSpecification, element: HTMLElement): HTMLElement {
    if (!interceptors) {
      return element
    }

    return interceptors?.reduce(
      (nextElement, it) => it.interceptor?.afterChildrenBuilt?.(injector, args, nextElement, it?.config) ?? nextElement,
      element
    )
  }

  /**
   * Get the interceptors for the config
   *
   * @internal
   *
   * @param config    The configuration for wiring up the dynamic element
   * @param injector  The injector used in the context where the service is being called
   *
   * @returns         A collection of interceptors and their auxillary configs. If there are no interceptors
   *                  configured, an empty list is returned instead.
   */
  getInterceptors(injector: Injector, config: DynamicElementConfig): InterceptorWithConfig[] {
    // If there are no interceptors configured, return an empty list
    if (!config?.interceptors) {
      return []
    }

    // Get the interceptors either from their value or from the DI
    return config?.interceptors?.map((it, index) => {
      let config

      if (typeof it === 'object' && !(it instanceof InjectionToken)) {
        let interceptorOptions = it as DynamicElementInterceptorOptions
        config = interceptorOptions?.config

        // Get the DI injected value for the class
        if (it?.useClass) {
          let interceptor = injector.get<typeof it.useClass>(it.useClass) as DynamicElementInterceptor
          return {
            interceptor,
            config
          }
        }

        // Get the existing provided value
        if (it?.useValue) {
          return {
            interceptor: it.useValue,
            config
          }
        }

        // Neither of the required provider methods are given, throw an error
        throw new Error(
          `DynamicElementInterceptorOptions at index ${ index } for the DynamicElementConfig with the type of ${ config.type } is invalid. `
            + `It must either contain a definition for useClass or useValue.`
        )
      }

      let interceptor = it instanceof InjectionToken ?
        injector.get(it) :
        injector.get<typeof it>(it) as DynamicElementInterceptor

      return {
        interceptor,
        config
      }
    })
  }

  /**
   * Get the matching configs for the provided type
   *
   * @param type  The type of element defined by it's specification
   *
   * @return      The registered context that matches the type provided in the spec
   */
  getConfig(type: string): DynamicElementConfig

  /**
   * Get the matching configs for the provided spec
   *
   * @param args  The specification of the element we want to dynamically create
   *
   * @return      The registered context that matches the type provided in the spec
   */
  getConfig(args: DynamicElementSpecification): DynamicElementConfig

  getConfig(args: string | DynamicElementSpecification): DynamicElementConfig {
    let type = typeof args === 'string' ?
      args :
      args?.type

    // Find the matching config for the arguments
    let matchingConfig = this.configs.find(it => it.type === type)

    // If there isn't a matching config try and find one that matches the aliases
    if (!matchingConfig) {
      let aliasedMatchingConfig = this.configs.find(it => it?.aliases?.some(alias => alias === type))

      // If there are no matching configs at all, raise an error
      if (!aliasedMatchingConfig) {
        throw new ReferenceError(
          `Unable to find configuration for a DynamicElement with a type of "${ type }", `
          + `please be sure to register it by adding it to the configs of DynamicElementModule.forRoot().`
        )
      }

      return aliasedMatchingConfig
    }

    return matchingConfig
  }

  /**
   * Get the default classes from a config
   *
   * @param config  The configuration for wiring up a dynamic element
   *
   * @returns       A collection of classes
   */
  private getDefaultClasses(config: DynamicElementConfig): string[] {
    let pendingValue = config.defaultClasses

    if (typeof pendingValue === 'string') {
      return pendingValue.split(/\s/)
    }

    return pendingValue ?? []
  }

  /**
   * A shortcut for getting the flattened configs
   *
   * @internal
   */
  get configs(): DynamicElementConfigs {
    return this._configs.flat()
  }
}
